import { useCallback } from "react";
import omit from "lodash/omit";
import {
  useCreateTransactionMutation,
  useDeleteTransactionMutation,
  useUpdateTransactionMutation,
} from "../../../services/transactions";
import {
  useCreateRecurringTransactionMutation,
  useDeleteRecurringTransactionMutation,
  useUpdateRecurringTransactionMutation,
} from "../../../services/recurringTransactions";
import {
  useAddLiabilityPaymentMutation,
  useDeleteLiabilityPaymentMutation,
  useUpdateLiabilityPaymentMutation,
} from "../../../services";
import { notify } from "../../../components/Notification";
import {
  UseTransactionsActionsProps,
  UseTransactionsActionsReturn,
} from "./types";
import {
  CreateTransactionsObject,
  TransactionTypeOptions,
} from "../panels/transactions/types";
import { formatDate, TransactionType } from "../../../utils";
import { getRecurringTransactionObject } from "../utils/transformers";

// Every write is an RTK Query mutation carrying its own tag invalidation, so there is no manual invalidateTags dispatch; catch blocks below exist only for UI control flow.
export const useTransactionsActions = ({
  accounts,
  panel,
  selectedTransaction,
  recurringTransactions,
  closePanel,
  closeDeleteModal,
  requestAccount,
}: UseTransactionsActionsProps): UseTransactionsActionsReturn => {
  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [deleteTransaction] = useDeleteTransactionMutation();
  const [createRecurringTransaction] = useCreateRecurringTransactionMutation();
  const [updateRecurringTransaction] = useUpdateRecurringTransactionMutation();
  const [deleteRecurringTransaction] = useDeleteRecurringTransactionMutation();
  const [addPayment] = useAddLiabilityPaymentMutation();
  const [updatePayment] = useUpdateLiabilityPaymentMutation();
  const [deletePayment] = useDeleteLiabilityPaymentMutation();

  // Deletion itself happens in handleDeleteConfirm; this just opens the modal via useTransactionsModals state.
  const handleDelete = useCallback((transaction: any) => {}, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedTransaction) {
      notify.error("No transaction selected for deletion.");
      return;
    }

    try {
      if (
        selectedTransaction.transactionType === TransactionType.LOAN_PAYMENT
      ) {
        const liabilityId = accounts.find(
          (account) => account.id === selectedTransaction.toAccountId,
        )?.liabilityId;
        if (!liabilityId) {
          notify.error("Liability account not found for this loan payment.");
          return;
        }
        await deletePayment({
          id: liabilityId,
          paymentId: selectedTransaction.id,
        }).unwrap();
      } else {
        await deleteTransaction(selectedTransaction.id).unwrap();
      }

      notify.success("Transaction deleted successfully");
      closeDeleteModal();
      if (requestAccount) {
        requestAccount();
      }
    } catch {
      // Global middleware shows the toast for mutation failures.
      closeDeleteModal();
    }
  }, [
    selectedTransaction,
    accounts,
    deleteTransaction,
    deletePayment,
    requestAccount,
    closeDeleteModal,
  ]);

  const handleDeleteRecurring = useCallback(async () => {
    if (!selectedTransaction?.id) {
      notify.error("No transaction selected for deletion.");
      return;
    }

    try {
      await deleteRecurringTransaction(selectedTransaction.id).unwrap();
      notify.success("Recurring transaction deleted successfully");
      closeDeleteModal();
    } catch {
      // Global middleware shows the toast for mutation failures.
    }
  }, [selectedTransaction, deleteRecurringTransaction, closeDeleteModal]);

  const handleLoanPayment = useCallback(
    async (data: CreateTransactionsObject, liabilityId: number) => {
      const fromAccountId = Number(data.fromAccount!.value);
      const payload = {
        amount: Number.parseFloat(data.amount),
        principalAmount: Number.parseFloat(data.principalAmount!),
        paymentDate: formatDate(data.transactionDate),
        fromAccountId,
        note: data.note,
      };

      if (data.isRecurring) {
        notify.error(
          `Recurring ${data.transactionType.label} transactions are not supported!`,
        );
        return;
      }

      if (panel.action === "edit") {
        await updatePayment({
          id: liabilityId,
          paymentId: data.id!,
          payload,
        }).unwrap();
        notify.success("Transaction edited successfully!");
      } else {
        await addPayment({ id: liabilityId, payload }).unwrap();
        notify.success("Transaction created successfully!");
      }
    },
    [panel.action, addPayment, updatePayment],
  );

  const handleTransactions = useCallback(
    async (data: CreateTransactionsObject) => {
      const payload = {
        ...omit(data, "id", "principalAmount", "interestAmount"),
        transactionDate: formatDate(data.transactionDate),
      };

      if (panel.action === "edit") {
        if (!panel.transaction?.id) {
          notify.error("Transaction ID is missing. Cannot edit transaction.");
          return;
        }

        if (data.isRecurring) {
          await updateRecurringTransaction({
            recurringTransactionId: panel.transaction.id,
            recurringTransaction: payload,
          }).unwrap();
        } else {
          await updateTransaction({
            transactionId: panel.transaction.id,
            transaction: payload,
          }).unwrap();
        }
        notify.success("Transaction edited successfully!");
      } else {
        if (data.isRecurring) {
          await createRecurringTransaction(payload).unwrap();
        } else {
          await createTransaction(payload).unwrap();
        }
        notify.success("Transaction created successfully!");
      }
    },
    [
      panel.action,
      panel.transaction,
      createTransaction,
      updateTransaction,
      createRecurringTransaction,
      updateRecurringTransaction,
    ],
  );

  const handleSubmit = useCallback(
    async (data: CreateTransactionsObject) => {
      const isLoanPayment =
        data.transactionType.value === TransactionTypeOptions.LOAN_PAYMENT;
      try {
        if (isLoanPayment) {
          const liabilityId = accounts.find(
            (account) => account.id === Number(data.toAccount?.value),
          )?.liabilityId;
          if (!liabilityId) {
            notify.error("Liability account not found for this loan payment.");
            return;
          }
          await handleLoanPayment(data, liabilityId);
        } else {
          await handleTransactions(data);
        }

        closePanel();

        if (requestAccount) {
          requestAccount();
        }
      } catch {
        // Global middleware shows the toast for mutation failures.
      }
    },
    [
      accounts,
      handleLoanPayment,
      handleTransactions,
      closePanel,
      requestAccount,
    ],
  );

  const handleEditRecurring = useCallback(
    (
      recurringTransactionId: number,
      openPanel: (
        action: "edit",
        transaction: CreateTransactionsObject,
      ) => void,
    ) => {
      const recurringTransaction = recurringTransactions.find(
        (rt) => rt.id === recurringTransactionId,
      );
      if (!recurringTransaction) {
        notify.error("Recurring transaction not found.");
        return;
      }

      const hideWarning =
        localStorage.getItem("hideRecurringEditWarning") === "true";

      if (hideWarning) {
        openPanel("edit", getRecurringTransactionObject(recurringTransaction));
      } else {
        // Returned (not opened directly) so the caller can show the warning first.
        return recurringTransaction;
      }
    },
    [recurringTransactions],
  );

  const handleConfirmRecurringEdit = useCallback(
    (
      pendingEdit: any,
      openPanel: (
        action: "edit",
        transaction: CreateTransactionsObject,
      ) => void,
    ) => {
      if (pendingEdit) {
        openPanel("edit", getRecurringTransactionObject(pendingEdit));
      }
    },
    [],
  );

  return {
    handleDelete,
    handleDeleteConfirm,
    handleDeleteRecurring,
    handleSubmit,
    handleEditRecurring,
    handleConfirmRecurringEdit,
  };
};
